import React from "react";
import { LuEye } from "react-icons/lu";
import cn from "../../utils/cn";

/**
 * MediaPreviewTrigger — wraps a media thumbnail and reveals an antd-style
 * "Preview" mask on hover, opening the caller's previewer on click.
 *
 * We render our own mask because the thumbnails pass `preview={false}` to antd's
 * `Image` (the shared `MediaPreview` replaces antd's lightbox), which also
 * removes antd's built-in hover mask.
 *
 * For a video thumbnail that shows native controls, pass `maskClassName` with a
 * bottom offset (e.g. "bottom-10") so the mask stops above the control bar and
 * play / scrub stay clickable.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The thumbnail
 * @param {() => void} props.onPreview - Opens the previewer
 * @param {string} [props.label] - Mask label (default "Preview")
 * @param {boolean} [props.disabled] - Render children bare, with no mask/click
 * @param {string} [props.className] - Extra classes for the wrapper
 * @param {string} [props.radiusClassName] - Match the thumbnail's corner radius
 * @param {string} [props.maskClassName] - Overrides the mask's inset, for
 *   thumbnails whose own controls must stay reachable
 * @param {boolean} [props.clickThrough] - Let clicks reach the child (native
 *   video controls) — the mask still opens the previewer on click
 */
const MediaPreviewTrigger = ({
  children,
  onPreview,
  label = "Preview",
  disabled = false,
  className = "",
  radiusClassName = "rounded",
  maskClassName = "inset-0",
  clickThrough = false,
}) => {
  if (disabled) return children ?? null;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      // With clickThrough the wrapper must not intercept — the mask carries the
      // click instead, leaving the child's own controls usable.
      onClick={clickThrough ? undefined : onPreview}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPreview?.();
        }
      }}
      className={cn(
        "group relative block w-full overflow-hidden",
        clickThrough ? "" : "cursor-pointer",
        radiusClassName,
        className
      )}
    >
      {children}
      {/* Fades in over the thumbnail. Normally pointer-events-none so the click
          lands on the wrapper; with clickThrough the mask itself takes the click
          and anything outside it (a video's control bar) stays interactive. */}
      <div
        onClick={clickThrough ? onPreview : undefined}
        className={cn(
          "absolute flex items-center justify-center gap-1.5",
          clickThrough ? "cursor-pointer" : "pointer-events-none",
          maskClassName,
          "bg-black/50 text-white opacity-0 transition-opacity duration-200",
          "group-hover:opacity-100 group-focus-visible:opacity-100",
          radiusClassName
        )}
      >
        <LuEye size={16} />
        <span className="text-xs font-medium">{label}</span>
      </div>
    </div>
  );
};

export default MediaPreviewTrigger;
